import { useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config";

export default function useCaptureAndUploadImage(token) {
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Captures an image from a video element and uploads it.
   * @param {Object} options - Options object.
   * @param {string} options.videoSelector - CSS selector for the video element (e.g., "#video").
   * @param {Function} options.onSuccess - Callback on successful upload; receives imageURL.
   * @param {Function} options.onDone - Callback after image is captured, before upload.
   * @param {Function} options.setCameraMode - Setter to exit camera mode (optional).
   * @param {Function} options.setShowUploadOptions - Setter to hide modal (optional).
   * @param {Function} options.setForm - Setter to update form with imageURL (optional).
   */
  const captureAndUpload = async ({
    videoSelector = "#video",
    onSuccess,
    onDone,
    setCameraMode,
    setShowUploadOptions,
    setForm,
  } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const videoElement = document.querySelector(videoSelector);
      if (!videoElement) throw new Error("Video element not found");

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      const imageDataUrl = canvas.toDataURL("image/png");
      setImagePreview(imageDataUrl);

      // Call after capture if needed
      if (onDone) onDone(imageDataUrl);

      // Stop the camera stream
      const stream = videoElement.srcObject;
      if (stream && stream.getTracks) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (setCameraMode) setCameraMode(false);

      // Send image to backend
      const response = await axios.post(
        `${API_BASE_URL}/patient/techPatientProfileUpload`,
        { image: imageDataUrl },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.imageURL) {
        if (setShowUploadOptions) setShowUploadOptions(false);
        if (setCameraMode) setCameraMode(false);
        if (setForm) {
          setForm((prevForm) => ({
            ...prevForm,
            imageURL: response.data.imageURL,
          }));
        }
        if (onSuccess) onSuccess(response.data.imageURL);
      }
    } catch (err) {
      setError(err);
      console.error("Error capturing or uploading image:", err);
    } finally {
      setLoading(false);
    }
  };

  return {
    imagePreview,
    loading,
    error,
    captureAndUpload,
    setImagePreview, // if you want to clear/reset preview externally
  };
}
